/* eslint-disable default-case */
import languages from 'dashboard/components/widgets/conversation/advancedFilterItems/languages';
import countries from 'shared/constants/countries';
import {
  generateCustomAttributeTypes,
  getActionOptions,
  getConditionOptions,
  getCustomAttributeInputType,
  getOperatorTypes,
  isACustomAttribute,
  getFileName,
  getDefaultConditions,
  getDefaultActions,
  filterCustomAttributes,
  generateAutomationPayload,
  getStandardAttributeInputType,
  isCustomAttribute,
  generateCustomAttributes,
} from 'dashboard/helper/automationHelper';
import FormsAPI from 'dashboard/api/integrations/forms';
import {
  EVENT_ACTION_TYPES,
  FORM_FIELD_ATTRIBUTE_TYPE,
  FORM_FIELD_OPERATORS,
  FORM_RESPONSE_EVENT,
  getRuleEvents,
} from 'dashboard/routes/dashboard/settings/automation/constants';
import { mapGetters } from 'vuex';

const FORM_ATTRIBUTE_KEY = 'form_id';
const FORM_RESPONSE_GROUP_KEY = 'form_response';

export default {
  data() {
    return {
      forms: [],
      formFields: [],
      // The form the loaded fields belong to, so switching back and forth does not refetch.
      formFieldsFormId: null,
    };
  },
  computed: {
    ...mapGetters({
      agents: 'agents/getAgents',
      campaigns: 'campaigns/getAllCampaigns',
      contacts: 'contacts/getContacts',
      inboxes: 'inboxes/getInboxes',
      labels: 'labels/getLabels',
      teams: 'teams/getTeams',
      slaPolicies: 'sla/getSLA',
      surveys: 'csatSurveys/getCsatSurveys',
      accountId: 'getCurrentAccountId',
      getAccount: 'accounts/getAccount',
    }),
    account() {
      return this.getAccount(this.accountId);
    },
    isFormResponseEvent() {
      return this.automation?.event_name === FORM_RESPONSE_EVENT;
    },
    automationRuleEvents() {
      const events = getRuleEvents.bind(this)();
      // A rule already built on the event keeps it, so turning Forms off does not blank the select.
      if (this.account?.forms_enabled || this.isFormResponseEvent) {
        return events;
      }
      return events.filter(event => event.key !== FORM_RESPONSE_EVENT);
    },
    filteredAutomationActionTypes() {
      const allowedActions = EVENT_ACTION_TYPES[this.automation?.event_name];
      return this.automationActionTypes.filter(action => {
        if (allowedActions && !allowedActions.includes(action.key)) {
          return false;
        }
        if (['add_sla', 'cancel_sla'].includes(action.key)) {
          return Boolean(this.account?.features?.sla);
        }
        return true;
      });
    },
    // The Form condition scopes the whole rule and decides whether answers can be conditioned on.
    formCondition() {
      if (!this.isFormResponseEvent) return null;

      return (
        this.automation.conditions.find(
          condition => condition.attribute_key === FORM_ATTRIBUTE_KEY
        ) || null
      );
    },
    formConditionOperator() {
      return this.formCondition?.filter_operator || null;
    },
    // The row holding the Form condition. -1 once it has been removed, which lets the rule be
    // scoped again from any row.
    formConditionIndex() {
      if (!this.isFormResponseEvent) return -1;

      return this.automation.conditions.findIndex(
        condition => condition.attribute_key === FORM_ATTRIBUTE_KEY
      );
    },
    formFieldAttributes() {
      const conditions =
        this.automationTypes[FORM_RESPONSE_EVENT]?.conditions || [];
      return conditions.filter(
        condition => condition.customAttributeType === FORM_FIELD_ATTRIBUTE_TYPE
      );
    },
    // Only set while the rule is scoped to exactly one form with Is; Is not leaves it null, which is
    // what keeps the answers out of the condition list.
    scopedFormId() {
      if (this.formConditionOperator !== 'equal_to') return null;

      const { values } = this.formCondition;
      const selected = Array.isArray(values) ? values[0] : values;
      return selected?.id || selected || null;
    },
    booleanFilterOptions() {
      return [
        {
          id: true,
          name: this.$t('FILTER.ATTRIBUTE_LABELS.TRUE'),
        },
        {
          id: false,
          name: this.$t('FILTER.ATTRIBUTE_LABELS.FALSE'),
        },
      ];
    },

    statusFilterOptions() {
      const statusFilters = this.$t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS');
      return [
        ...Object.keys(statusFilters).map(status => {
          return {
            id: status,
            name: statusFilters[status].TEXT,
          };
        }),
      ];
    },
  },
  watch: {
    'automation.event_name': {
      handler(eventName) {
        if (eventName === FORM_RESPONSE_EVENT) this.fetchForms();
      },
      immediate: true,
    },
    // Is takes one form and Is not takes many, so the selection is reset rather than carried across
    // a shape it no longer fits.
    formConditionOperator(operator, previousOperator) {
      // Null means the row is no longer the Form condition, which resetFilter already cleared.
      if (!operator || !previousOperator || operator === previousOperator) {
        return;
      }

      this.formCondition.values = operator === 'equal_to' ? '' : [];
    },
    scopedFormId(formId, previousFormId) {
      if (!this.isFormResponseEvent) return;
      // Answers belong to the form they were asked on, so a different form drops them. Nothing is
      // dropped on the first resolution, which is the edit screen loading a saved rule.
      if (previousFormId) this.removeFormFieldConditions();

      if (!formId) {
        this.setFormFieldConditions([]);
        return;
      }
      this.fetchFormFields(formId);
    },
    'automation.actions'(e) {
      this.automationActionTypes.forEach(action => {
        if (action.key === 'assign_agent')
          action.disabled = e.some(
            action => action.action_name === 'assign_agent'
          );
        if (action.key === 'assign_team')
          action.disabled = e.some(
            action => action.action_name === 'assign_team'
          );
      });
    },
  },
  methods: {
    getFileName,
    onEventChange() {
      this.setFormFieldConditions([]);
      this.automation.conditions = getDefaultConditions(
        this.automation.event_name
      );
      this.automation.actions = getDefaultActions();
    },
    getAttributes(key, index = null) {
      const attributes = this.automationTypes[key].conditions.map(x => {
        // d99d
        return {
          ...x,
          name: x.attributeI18nKey
            ? this.$t(`FILTER.ATTRIBUTES.${x.attributeI18nKey}`)
            : x.name,
        };
      });
      if (key !== FORM_RESPONSE_EVENT || index === null) return attributes;

      return this.formResponseAttributes(attributes, index);
    },
    // A rule is scoped by exactly one Form condition, so the row that holds it offers only Form and
    // every other row offers only the answers of the form it picked.
    formResponseAttributes(attributes, index) {
      if (this.formConditionIndex === -1) return attributes;
      if (index === this.formConditionIndex) {
        return attributes.filter(
          attribute => attribute.key === FORM_ATTRIBUTE_KEY
        );
      }
      return attributes.filter(
        attribute => attribute.customAttributeType === FORM_FIELD_ATTRIBUTE_TYPE
      );
    },
    getInputType(key, condition = null) {
      // Is takes a single form and Is not takes many, so the Form condition's input follows its
      // operator instead of a fixed type.
      if (key === FORM_ATTRIBUTE_KEY) {
        const operator =
          condition?.filter_operator || this.formConditionOperator;
        return operator === 'not_equal_to' ? 'multi_select' : 'search_select';
      }
      const formField = this.formFieldDefinition(key);
      if (formField) return formField.inputType;

      const customAttribute = isACustomAttribute(this.allCustomAttributes, key);
      if (customAttribute) {
        return getCustomAttributeInputType(
          customAttribute.attribute_display_type
        );
      }
      const type = this.getAutomationType(key);
      return type.inputType;
    },
    getOperators(key) {
      const formField = this.formFieldDefinition(key);
      if (formField) return formField.filterOperators;

      if (this.mode === 'edit') {
        const customAttribute = isACustomAttribute(
          this.allCustomAttributes,
          key
        );
        if (customAttribute) {
          return getOperatorTypes(customAttribute.attribute_display_type);
        }
      }
      const type = this.getAutomationType(key);
      return type.filterOperators;
    },
    getAutomationType(key) {
      return this.automationTypes[this.automation.event_name].conditions.find(
        condition => condition.key === key
      );
    },
    getCustomAttributeType(key) {
      return this.getAutomationType(key)?.customAttributeType || '';
    },
    getConditionDropdownValues(type) {
      // form_id belongs to the form response event alone, so no other event can reach these.
      if (type === FORM_ATTRIBUTE_KEY) return this.forms;

      const formField = this.formFieldDefinition(type);
      if (formField) return formField.options || [];

      const {
        agents,
        allCustomAttributes: customAttributes,
        booleanFilterOptions,
        campaigns,
        contacts,
        inboxes,
        statusFilterOptions,
        teams,
        labels,
      } = this;
      return getConditionOptions({
        agents,
        booleanFilterOptions,
        campaigns,
        contacts,
        customAttributes,
        inboxes,
        statusFilterOptions,
        teams,
        languages,
        countries,
        labels,
        type,
      });
    },
    appendNewCondition() {
      if (this.isFormResponseEvent) {
        this.appendNewFormResponseCondition();
        return;
      }
      this.automation.conditions.push(
        ...getDefaultConditions(this.automation.event_name)
      );
    },
    // Only the first condition picks the form; the rest are built on its answers.
    appendNewFormResponseCondition() {
      if (this.formConditionIndex === -1) {
        this.automation.conditions.push(
          ...getDefaultConditions(FORM_RESPONSE_EVENT)
        );
        return;
      }
      const [field] = this.formFieldAttributes;
      if (!field) {
        this.showAlert(this.$t('AUTOMATION.FORM_RESPONSE.SELECT_FORM_FIRST'));
        return;
      }
      this.automation.conditions.push({
        attribute_key: field.key,
        filter_operator: field.filterOperators[0].value,
        values: '',
        query_operator: 'and',
        custom_attribute_type: FORM_FIELD_ATTRIBUTE_TYPE,
      });
    },
    appendNewAction() {
      this.automation.actions.push(...getDefaultActions());
    },
    removeFilter(index) {
      if (this.automation.conditions.length <= 1) {
        this.showAlert(this.$t('AUTOMATION.CONDITION.DELETE_MESSAGE'));
      } else {
        this.automation.conditions.splice(index, 1);
      }
    },
    removeAction(index) {
      if (this.automation.actions.length <= 1) {
        this.showAlert(this.$t('AUTOMATION.ACTION.DELETE_MESSAGE'));
      } else {
        this.automation.actions.splice(index, 1);
      }
    },
    submitAutomation() {
      this.$v.$touch();
      if (this.$v.$invalid) return;
      const automation = generateAutomationPayload(this.automation);
      this.$emit('saveAutomation', automation, this.mode);
    },
    resetFilter(index, currentCondition) {
      const operators =
        this.getAutomationType(currentCondition.attribute_key)
          ?.filterOperators || [];
      if (operators.length) {
        this.automation.conditions[index].filter_operator = operators[0].value;
      }
      this.automation.conditions[index].values = '';
    },
    showUserInput(type) {
      return !(type === 'is_present' || type === 'is_not_present');
    },
    showActionInput(action) {
      if (action === 'send_email_to_team' || action === 'send_message')
        return false;
      const type = this.automationActionTypes.find(
        i => i.key === action
      ).inputType;
      return !!type;
    },
    resetAction(index) {
      this.automation.actions[index].action_params = [];
    },
    manifestConditions(automation) {
      const customAttributes = filterCustomAttributes(this.allCustomAttributes);
      const conditions = automation.conditions.map(condition => {
        // A form field is checked first: its key comes from the forms app and could collide with a
        // contact custom attribute of the same name.
        const formField = this.formFieldDefinition(condition.attribute_key);
        const customAttr = formField
          ? null
          : isCustomAttribute(customAttributes, condition.attribute_key);
        let inputType = 'plain_text';
        if (formField) {
          inputType = formField.inputType;
        } else if (customAttr) {
          inputType = getCustomAttributeInputType(customAttr.type);
        } else {
          inputType = getStandardAttributeInputType(
            this.automationTypes,
            automation.event_name,
            condition.attribute_key
          );
        }
        if (
          inputType === 'plain_text' ||
          inputType === 'date' ||
          inputType === 'number'
        ) {
          return {
            ...condition,
            values: condition.values[0],
          };
        }
        if (inputType === 'comma_separated_plain_text') {
          return {
            ...condition,
            values: condition.values.join(','),
          };
        }
        return {
          ...condition,
          query_operator: condition.query_operator || 'and',
          values: [
            ...this.getConditionDropdownValues(condition.attribute_key),
          ].filter(item => [...condition.values].includes(item.id)),
        };
      });
      return conditions;
    },
    generateActionsArray(action) {
      const params = action.action_params;
      let actionParams = [];
      const inputType = this.automationActionTypes.find(
        item => item.key === action.action_name
      ).inputType;
      if (inputType === 'multi_select' || inputType === 'search_select') {
        actionParams = [
          ...this.getActionDropdownValues(action.action_name),
        ].filter(item => [...params].includes(item.id));
      } else if (inputType === 'single_select') {
        // d99d
        actionParams = [...this.getActionDropdownValues(action.action_name)]
          .filter(item => [...params].includes(item.id))
          .shift();
      } else if (inputType === 'team_message') {
        actionParams = {
          team_ids: [
            ...this.getActionDropdownValues(action.action_name),
          ].filter(item => [...params[0].team_ids].includes(item.id)),
          message: params[0].message,
        };
      } else actionParams = [...params];
      return actionParams;
    },
    manifestActions(automation) {
      let actionParams = [];
      const actions = automation.actions.map(action => {
        if (action.action_params.length) {
          actionParams = this.generateActionsArray(action);
        }
        return {
          ...action,
          action_params: actionParams,
        };
      });
      return actions;
    },
    formatAutomation(automation) {
      this.automation = {
        ...automation,
        conditions: this.manifestConditions(automation),
        actions: this.manifestActions(automation),
      };
    },
    getActionDropdownValues(type) {
      // d99d
      switch (type) {
        case 'assign_agent':
          return [
            ...this.$store.getters['agents/getAgents'],
            { id: 'nil', name: this.$t('AUTOMATION.ACTION.UNASSIGNED') },
          ];
        case 'assign_team':
          return [
            ...this.teams,
            { id: 'nil', name: this.$t('AUTOMATION.ACTION.UNASSIGNED') },
          ];
        case 'change_status':
          const { open, pending } = this.$t(
            'CHAT_LIST.CHAT_STATUS_ADVANCED_FILTER_ITEMS'
          );
          return [
            { id: 'open', name: open.TEXT },
            { id: 'pending', name: pending.TEXT },
          ];
        case 'mute_conversation': {
          const { options } = this.$t('MUTE_DURATIONS');

          return Object.values(options).map(duration => ({
            id: duration.VALUE,
            name: duration.LABEL,
          }));
        }
      }
      // .
      const { agents, labels, teams, slaPolicies, surveys } = this;
      return getActionOptions({
        agents,
        labels,
        teams,
        slaPolicies,
        surveys,
        languages,
        type,
      });
    },
    formFieldDefinition(key) {
      return this.automationTypes[FORM_RESPONSE_EVENT]?.conditions.find(
        condition =>
          condition.key === key &&
          condition.customAttributeType === FORM_FIELD_ATTRIBUTE_TYPE
      );
    },
    // The answers become selectable the same way custom attributes do: appended to the event's own
    // condition list under a heading, so every lookup that reads automationTypes keeps working.
    setFormFieldConditions(fields) {
      this.formFields = fields;
      const eventTypes = this.automationTypes[FORM_RESPONSE_EVENT];
      if (!eventTypes) return;

      const baseConditions = eventTypes.conditions.filter(
        condition =>
          condition.key !== FORM_RESPONSE_GROUP_KEY &&
          condition.customAttributeType !== FORM_FIELD_ATTRIBUTE_TYPE
      );
      if (!fields.length) {
        eventTypes.conditions = baseConditions;
        return;
      }
      eventTypes.conditions = [
        ...baseConditions,
        {
          key: FORM_RESPONSE_GROUP_KEY,
          name: this.$t('AUTOMATION.CONDITION.FORM_RESPONSE_ATTR_LABEL'),
          disabled: true,
        },
        ...fields.map(field => ({
          key: field.key,
          name: field.name,
          inputType: field.type,
          filterOperators:
            FORM_FIELD_OPERATORS[field.type] || FORM_FIELD_OPERATORS.plain_text,
          customAttributeType: FORM_FIELD_ATTRIBUTE_TYPE,
          options: field.options || [],
        })),
      ];
    },
    removeFormFieldConditions() {
      const conditions = this.automation.conditions.filter(
        condition =>
          condition.custom_attribute_type !== FORM_FIELD_ATTRIBUTE_TYPE
      );
      this.automation.conditions = conditions.length
        ? conditions
        : getDefaultConditions(FORM_RESPONSE_EVENT);
    },
    async fetchForms() {
      if (this.forms.length) return;

      try {
        const { data } = await FormsAPI.getFormsList();
        this.forms = (data || []).map(form => ({
          id: form.publicId,
          name: form.name,
        }));
      } catch (error) {
        this.showAlert(this.$t('AUTOMATION.FORM_RESPONSE.FORMS_ERROR'));
      }
    },
    async fetchFormFields(formPublicId) {
      if (this.formFieldsFormId === formPublicId) return;

      try {
        const { data } = await FormsAPI.getFormFields(formPublicId);
        this.setFormFieldConditions(data.fields || []);
        this.formFieldsFormId = formPublicId;
      } catch (error) {
        this.setFormFieldConditions([]);
        this.formFieldsFormId = null;
        this.showAlert(this.$t('AUTOMATION.FORM_RESPONSE.FIELDS_ERROR'));
      }
    },
    // The edit screen hydrates a saved rule against the condition list, so the form it is scoped to
    // has to be resolved before that list is read.
    async loadFormFieldsForAutomation(automation) {
      if (automation?.event_name !== FORM_RESPONSE_EVENT) return;

      await this.fetchForms();
      const formCondition = (automation.conditions || []).find(
        condition => condition.attribute_key === FORM_ATTRIBUTE_KEY
      );
      if (formCondition?.filter_operator !== 'equal_to') return;

      const values = formCondition.values;
      const formId = Array.isArray(values) ? values[0] : values;
      if (formId) await this.fetchFormFields(formId);
    },
    manifestCustomAttributes() {
      const conversationCustomAttributesRaw = this.$store.getters[
        'attributes/getAttributesByModel'
      ]('conversation_attribute');

      const contactCustomAttributesRaw =
        this.$store.getters['attributes/getAttributesByModel'](
          'contact_attribute'
        );
      const conversationCustomAttributeTypes = generateCustomAttributeTypes(
        conversationCustomAttributesRaw,
        'conversation_attribute'
      );
      const contactCustomAttributeTypes = generateCustomAttributeTypes(
        contactCustomAttributesRaw,
        'contact_attribute'
      );
      let manifestedCustomAttributes = generateCustomAttributes(
        conversationCustomAttributeTypes,
        contactCustomAttributeTypes,
        this.$t('AUTOMATION.CONDITION.CONVERSATION_CUSTOM_ATTR_LABEL'),
        this.$t('AUTOMATION.CONDITION.CONTACT_CUSTOM_ATTR_LABEL')
      );
      this.automationTypes.message_created.conditions.push(
        ...manifestedCustomAttributes
      );
      this.automationTypes.conversation_created.conditions.push(
        ...manifestedCustomAttributes
      );
      this.automationTypes.conversation_updated.conditions.push(
        ...manifestedCustomAttributes
      );
      this.automationTypes.conversation_opened.conditions.push(
        ...manifestedCustomAttributes
      );
    },
  },
};
